import { useEffect, useRef, useState } from "react";
import { Socket, io } from "socket.io-client";
import { useUser } from "./useUser";
import config from "../config";
import eventEmitter from "../utils/eventEmitter";

interface ProjectMessageParam {
    content: string;
    userId: number;
    timestamp: number;
}

export interface BriefNoteParam {
    briefId: number;
}

interface ToClientEvents {
    noArg: () => void;
    basicEmit: (a: number, b: string, c: Buffer) => void;
    withAck: (d: string, callback: (e: number) => void) => void;
    notification: (notificationParam: string) => void;
    projectMessage: (projectMessageParam: string) => void;
    briefNote: (briefNoteParam: string) => void;
}

export default () => {
    const { user } = useUser();
    const host = config.HOST;
    const socketRef = useRef<Socket<ToClientEvents> | null>(null);
    const [socketState, setSocketState] = useState<Socket<ToClientEvents> | null>(null);

    const handleProjectMessage = (param: string) => {
        try {
            const parsed: ProjectMessageParam = JSON.parse(param);
            eventEmitter.emit("projectMessage", parsed);
        } catch {
            // swallow parse errors
        }
    };

    // A brief's notes changed. Carries only { briefId } — rooms are joined
    // without auth, so the note text never rides the socket; listeners refetch.
    const handleBriefNote = (param: string) => {
        try {
            const parsed: BriefNoteParam = JSON.parse(param);
            eventEmitter.emit("briefNote", parsed);
        } catch {
            // swallow parse errors
        }
    };

    const handleNotification = (param: string) => {
        try {
            eventEmitter.emit("notification", JSON.parse(param));
        } catch {
            // swallow parse errors
        }
    };

    const handleDownload = (param: string) => {
        try {
            eventEmitter.emit("download", JSON.parse(param));
        } catch {
            // swallow parse errors
        }
    };

    const handleRefresh = (param: string) => {
        try {
            eventEmitter.emit("importReload", JSON.parse(param));
        } catch {
            // swallow parse errors
        }
    };

    // Live RMN crawl deltas ({crawlId, stats, nodes, links}), emitted to the
    // room `crawl:<id>` — join/leave via the returned socket instance.
    const handleCrawlProgress = (param: string) => {
        try {
            eventEmitter.emit("crawlProgress", JSON.parse(param));
        } catch {
            // swallow parse errors
        }
    };

    useEffect(() => {
        // Disconnect if user logged out
        if (!user) {
            if (socketRef.current) {
                socketRef.current.off("projectMessage", handleProjectMessage);
                socketRef.current.off("notification", handleNotification);
                //socketRef.current.disconnect();
                //socketRef.current = null;
                //setSocketState(null);
            }
            //return;
        }

        // Avoid duplicate connection if already established
        if (socketRef.current) return;

        const socket = io(host as string, {
            transports: ["websocket"]
        });
        socketRef.current = socket;
        setSocketState(socket);

        socket.on("projectMessage", handleProjectMessage);
        socket.on("briefNote", handleBriefNote);
        socket.on("notification", handleNotification);
        socket.on("download", handleDownload);
        socket.on("importReload", handleRefresh);
        socket.on("crawlProgress", handleCrawlProgress);

        socket.on("connect_error", (err) => {
            // optional: report/log
            // console.warn('Socket connect_error', err.message);
        });

        return () => {
            socket.off("projectMessage", handleProjectMessage);
            socket.off("briefNote", handleBriefNote);
            socket.off("notification", handleNotification);
            socket.off("download", handleDownload);
            socket.off("importReload", handleRefresh);
            socket.off("crawlProgress", handleCrawlProgress);
            socket.disconnect();
            if (socketRef.current === socket) {
                socketRef.current = null;
                setSocketState(null);
            }
        };
    }, [user, host]);

    return { socket: socketState };
};
